import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-select/dist/css/bootstrap-select.min.css';

import { yupResolver } from '@hookform/resolvers/yup';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useAuth, useGetStates } from '@/api';
import { useGetOtp } from '@/api/auth/getOtp';
import { useVerifyOtp } from '@/api/forex';
import { useCreateLead } from '@/api/forex/useCreateLead';
import LeftArrowIcon from '@/assets/images/icons/black-arrow-left.svg';
import CloseIcon from '@/assets/images/icons/close-icon.svg';
// import type { LeadStore } from '@/common/components/Forex/components/landing-page/PreLogin';
import type { GetOtpArgsType } from '@/types';

import { useItemsStore, useNotificationStore } from '../../store';
import BasicDropdown from '../BasicDropdown';
import { FieldWrapper } from '../Form';
import { OtpInput } from '../Form/input/OTPInput';
import ButtonLoader from '../UiLoader/ButtonLoader';
import { emailRegex } from './utils';

export type CallBackFormType = {
  name: string;
  mobileNumber: string;
  state: string;
  city: string;
  isConsentAccepted: boolean;
  email: string;
};

type CallbackFormProps = {
  activeStep: number;
  handleStep: (step: number) => void;
  leadState: any;
  setLeadState?: any;
};

export enum CityEnums {
  BANGALORE = 'BANGALORE',
  MUMBAI = 'MUMBAI',
  KOCHI = 'KOCHI',
  TRIVANDRUM = 'TRIVANDRUM',
  KOLKATA = 'KOLKATA',
  JAIPUR = 'JAIPUR',
  DELHI_NCR = 'DELHI_NCR',
  AHMEDABAD = 'AHMEDABAD',
  INDORE = 'INDORE',
  CHANDIGARH = 'CHANDIGARH',
  NAVIMUMBAI = 'NAVIMUMBAI',
  BHUBANESHWAR = 'BHUBANESHWAR',
  PUNE = 'PUNE',
  CHENNAI = 'CHENNAI',
  CALICUT = 'CALICUT',
  HYDERABAD = 'HYDERABAD',
}

export enum UtmEnums {
  FOREX_GOOGLE_SEARCH = 'GOOGLE_SEARCH',
}

const phoneRegExp = /^[6-9][0-9]{9}$/;
const textRegExp = /^(?! )[A-Za-z ]+(?<! )$/;

const callBackFormSchema = yup.object({
  name: yup
    .string()
    .matches(textRegExp, 'Enter valid name')
    .required('Name is required'),
  mobileNumber: yup
    .string()
    .matches(phoneRegExp, 'Enter valid mobile number')
    .required('Mobile number is required'),
  city: yup
    .string()
    .matches(textRegExp, 'Enter valid city')
    .required('City is required'),
  state: yup.string().required('State is required'),
  isConsentAccepted: yup
    .boolean()
    .required('Please accept the terms & conditions'),
  email: yup
    .string()
    .required('Email is required')
    .matches(emailRegex, 'Invalid email')
    .email('Enter valid email'),
});

const CallbackForm = (props: CallbackFormProps) => {
  // capturing the utms
  const { userId } = useAuth();

  const router = useRouter();
  const [leadSource, setLeadSource] = useState<string>('FOREX_MLP');
  const [leadCity, setLeadCity] = useState<CityEnums | ''>('');

  const getSourceFromUtmLink = (url: string, cityFormQuery: string) => {
    let location: CityEnums;
    if (cityFormQuery) {
      location = cityFormQuery
        .toLocaleUpperCase()
        .replace('-', '_') as CityEnums;
      setLeadCity(CityEnums[location]);
    }
    const utmSource = url.match(/utm_source=([+\w]*)/g);
    const utmMedium = url.match(/utm_medium=([+%_\w]*)/g);
    const utmCampaign = url.match(/utm_campaign=([+%_\w]*)/g);
    const utmId = url.match(/utm_id=([+%_\w]*)/g);
    const matcher = `${utmSource}&${utmMedium}&${utmCampaign}&${utmId}`;

    switch (matcher) {
      case 'utm_source=GOOGLE&utm_medium=Google+Search+Forex&utm_campaign=Google+Search+Forex&utm_id=Google+Search':
        setLeadSource(UtmEnums.FOREX_GOOGLE_SEARCH);
        break;
      default:
        setLeadSource('FOREX_MLP');
        break;
    }
  };

  useEffect(() => {
    if (router) {
      getSourceFromUtmLink(router.asPath, (router.query.city as string) || '');
    }
  }, [router]);

  const { activeStep, handleStep, leadState, setLeadState } = props;
  const { addNotification } = useNotificationStore();

  const [userDetails, setUserDetails] = useItemsStore((state) => [
    state.userDetails,
    state.setUserDetails,
  ]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields },
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      name: '',
      mobileNumber: '',
      city: '',
      state: '',
      email: '',
      isConsentAccepted: true,
    },
    // defaultValues: {
    //   name: userDetails.userName,
    //   mobileNumber: userDetails.userPhone,
    //   city: userDetails.userCity,
    //   state: states[0],
    //   email: userDetails.userEmail,
    //   isConsentAccepted: true,
    // },
    mode: 'onChange',
    resolver: yupResolver(callBackFormSchema),
  });

  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(0);
  const [showOtpError, setShowOtpError] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const getOtp = useGetOtp();
  const { states } = useGetStates();
  const openOtpModalRef = useRef<HTMLButtonElement>(null);
  const closeOtpModalRef = useRef<HTMLButtonElement>(null);
  const { verifyOtp, isLoading: isOtpVerifying } = useVerifyOtp();
  const { createLead } = useCreateLead();
  const [leadVersion, setLeadVersion] = useState<'v0' | 'v1'>('v0');
  // const { refetch: refetchCurrencies } = useCurrencyRates();
  const [startTimer, setStartTimer] = useState(false);

  useEffect(() => {
    if (userId) {
      setLeadVersion('v1');
    }
  }, [userId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (startTimer) {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        }
        if (seconds === 0) {
          if (minutes === 0) {
            clearInterval(interval);
          } else {
            setSeconds(59);
            setMinutes(minutes - 1);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [minutes, seconds, startTimer]);

  const createLeadFn = async () => {
    const shapeRequestBody = {
      transactionItems: leadState.items.map((item: any) => ({
        transactionType: item.transactionType,
        currency: item.currency,
        amount: item.forexAmount,
        exchangeRate: item.exchangeRate,
        exchangeTime: item.exchangeTime,
      })),
      userName: userDetails.userName,
      userPhone: userDetails.userPhone,
      userEmail: userDetails.userEmail,
      userCity: userDetails.userCity,
      userState: userDetails.userState,
      ...(!userId && { otpSessionId: sessionId }),
      appVersion: 0,
      metadata: [
        {
          key: 'utm_source',
          value: leadSource,
        },
        {
          key: 'city',
          value: leadCity || 'NA',
        },
      ],
    };
    const { data: asd } = await createLead({
      payload: shapeRequestBody,
      leadVersion,
    });
    if (asd) {
      const getCurrentPath = router.pathname;
      router.push(`${getCurrentPath}/thank-you`);
    }
  };

  const validateOtp = async () => {
    const verifyOtpRequestBody = {
      sessionId,
      recipientMobileNumber: userDetails.userPhone,
      recipientEmailId: userDetails.userEmail,
      otp: otp.join(''),
    };
    const { data } = await verifyOtp(verifyOtpRequestBody);
    if (data.data.secretVerificationStatus === 'VERIFICATION_PASSED') {
      setOtp(new Array(6).fill(''));
      setUserDetails({
        userName: '',
        userPhone: '',
        userCity: '',
        userState: '',
        userEmail: ' ',
      });
      setValue('name', '');
      setValue('mobileNumber', '');
      setValue('city', '');
      setValue('state', '');
      setValue('email', '');
      setLeadState({
        items: [],
      });
      closeOtpModalRef.current?.click();
      createLeadFn();
    } else {
      addNotification({
        type: 'error',
        message: 'Invalid OTP',
        title: '',
      });
    }
  };

  const resetTimer = () => {
    setMinutes(1);
    setSeconds(0);
  };

  const resendOtp = async () => {
    setOtp(new Array(6).fill(''));
    resetTimer();
    setShowOtpError(false);
    const getOtpRequestBody: GetOtpArgsType = {
      recipientMobileNumber: userDetails.userPhone,
    };
    const res = await getOtp.mutateAsync(getOtpRequestBody);
    setSessionId(res?.data?.data.sessionId);
  };
  const handleOtpSubmit = () => {
    const isOtpFilled = otp.every((value: any) => value !== '');
    if (isOtpFilled) {
      setShowOtpError(false);
      validateOtp();
    } else {
      setShowOtpError(true);
    }
  };

  const onSubmit = async (data: CallBackFormType) => {
    if (!data.isConsentAccepted) {
      addNotification({
        title: '',
        type: 'error',
        message: 'Please accept the terms & conditions',
      });
    }
    setUserDetails({
      userName: data.name,
      userEmail: data.email,
      userPhone: data.mobileNumber,
      userCity: data.city,
      userState: data.state,
    });
    setOtp(new Array(6).fill(''));
    if (userId) {
      createLeadFn();
    } else {
      openOtpModalRef.current?.click();
      setStartTimer(!startTimer);
      const getOtpRequestBody: GetOtpArgsType = {
        recipientMobileNumber: data.mobileNumber,
      };
      const res = await getOtp.mutateAsync(getOtpRequestBody);
      setSessionId(res.data.data.sessionId);
    }
  };

  return (
    <>
      <div className="callback-card" id="callbackform">
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className={`stepper--content active`}>
            <div className="callback-title-div">
              <button
                type="button"
                onClick={async () => {
                  setUserDetails({
                    userName: userDetails.userName,
                    userEmail: userDetails.userEmail,
                    userPhone: userDetails.userPhone,
                    userCity: userDetails.userCity,
                    userState: userDetails.userState,
                  });
                  // await refetchCurrencies();
                  handleStep(activeStep - 1);
                }}
              >
                <Image
                  style={{ color: undefined }}
                  src={LeftArrowIcon}
                  alt="left-icon"
                  className="callback-left-arrow"
                />
              </button>
              <p className="login__title text--black text--bold m-0 text-left">
                Request a Call back
              </p>
            </div>
            <div className="input-container">
              <FieldWrapper label={'Name'} error={errors.name}>
                <div className="input-form">
                  <input
                    type="text"
                    placeholder="Enter name"
                    {...register('name')}
                  />
                </div>
              </FieldWrapper>
            </div>
            <div className="input-container">
              <FieldWrapper label={'Email'} error={errors.email}>
                <div className="input-form">
                  <input
                    type="text"
                    placeholder="Enter Email"
                    {...register('email')}
                  />
                </div>
              </FieldWrapper>
            </div>
            <div className="input-container">
              <FieldWrapper label={'Mobile Number'} error={errors.mobileNumber}>
                <div className="input-form">
                  <input
                    type="text"
                    placeholder="Enter mobile number"
                    {...register('mobileNumber')}
                  />
                </div>
              </FieldWrapper>
            </div>
            <div className="input-container">
              <FieldWrapper label={'City'} error={errors.city}>
                <div className="input-form">
                  <input
                    type="text"
                    placeholder="Enter city"
                    {...register('city')}
                  />
                </div>
              </FieldWrapper>
            </div>
            <div className="input-container add-border">
              <BasicDropdown
                label="State"
                options={[...states.map((state) => state.stateName)]}
                placeholder="Select State"
                value={watch('state')}
                onChange={(stateThing) => {
                  setValue('state', stateThing, {
                    shouldDirty: true,
                  });
                  if (dirtyFields.state) {
                    trigger('state');
                  }
                }}
                errorMessage={errors.state?.message || ''}
              />
            </div>
            <div className="callback-consent">
              <input
                type="checkbox"
                className="callback-input"
                {...register('isConsentAccepted')}
              />
              <p className="login__terms text--gray text--regular">
                I agree to Muthoot FinCorp ONE{' '}
                <Link
                  href="/terms-and-conditions"
                  target="_self"
                  rel="noreferrer noopener"
                  className="text--darkBlue text--semi blue-text"
                  id="forex_terms-and-conditions"
                >
                  terms & conditions
                </Link>{' '}
                .
              </p>
            </div>
            <div className="action-container">
              <button
                disabled={getOtp.isLoading}
                type="submit"
                className={`custom__button custom__button--dark text--bold w-100 blue position-relative
                  ${getOtp.isLoading ? 'disabled' : ''} `}
              >
                {userId ? 'Submit Request' : 'Get OTP'}
                {getOtp.isLoading && <ButtonLoader />}
              </button>
            </div>
          </div>
        </form>
      </div>

      <button
        type="button"
        data-target="#otpModal"
        data-toggle="modal"
        className="hidden"
        ref={openOtpModalRef}
      ></button>

      <div
        className="modal loanDetails__modal repayModal visitSchedule login pledge loginFirstModal otpModal"
        id="otpModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="otpModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-static"
          role="document"
        >
          <div className="modal-content">
            <div className="modal__title justify-content-end">
              <button data-dismiss="modal" ref={closeOtpModalRef}>
                <Image
                  style={{ color: undefined }}
                  className="img-fluid"
                  src={CloseIcon}
                  alt="close icon"
                />
              </button>
            </div>
            <div className={`stepper--content otpContent active`}>
              <p className="login__title text--black text--bold text-center">
                Enter OTP
              </p>
              <p className="text--regular text--gray login__desc text-center">
                Please enter the OTP sent to <br /> {userDetails.userPhone}.{' '}
                <button
                  className="text--darkBlue text--semi blue-text"
                  onClick={() => {
                    setOtp(new Array(6).fill(''));
                    closeOtpModalRef.current?.click();
                    resetTimer();
                  }}
                >
                  Change
                </button>
              </p>
              <div className="input-container">
                <div className="flex-column h-auto w-full items-end justify-end">
                  <OtpInput setOtp={setOtp} otp={otp} />
                  {showOtpError ? (
                    <p className="field-wrapper-error-message">Invalid OTP</p>
                  ) : null}
                </div>
              </div>
              <div className="action-container">
                <button
                  disabled={isOtpVerifying}
                  type="submit"
                  className={`custom__button custom__button--dark text--bold w-100 stepperNext blue position-relative
                  ${isOtpVerifying ? 'disabled' : ''}`}
                  onClick={handleOtpSubmit}
                >
                  Verify
                  {isOtpVerifying && <ButtonLoader />}
                </button>
              </div>
              <p className="text--black text--semi login__existing text-center">
                {seconds !== 0 || minutes !== 0 ? (
                  <>
                    <span className="text--gray">Resend OTP in </span>
                    {minutes < 10 ? `0${minutes}` : minutes}:
                    {seconds < 10 ? `0${seconds}` : seconds}
                  </>
                ) : (
                  <button
                    className="text--darkBlue text--semi cursor-pointer text-center"
                    onClick={resendOtp}
                  >
                    Resend
                  </button>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {/* <div
        className="modal loanDetails__modal repayModal visitSchedule login pledge loginFirstModal otpModal"
        id="successModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="successModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-static"
          role="document"
        >
          <div className="modal-content">
            <div className="modal__title">
              <button ref={closeSuccessModalRef} data-dismiss="modal"></button>
            </div>
            <div className={`stepper--content active`}>
              <div className="sucess-modal-body mt-0">
                <Image 
style={{ color: undefined }} src={SuccessIcon} alt="success-icon" />
                <p className="price-modal-value m-0 text-center">
                  Thanks for sharing the details
                </p>
                <p className="sucess-modal-text-one m-0">
                  Our team will contact you within 24 hours. <br /> In the
                  meantime, you can also reach out to us at <br />
                  <span className="brand-color">
                    <a href="mailto:customercare@muthoot.com">
                      customercare@muthoot.com
                    </a>
                  </span>{' '}
                  or{' '}
                  <span className="brand-color">
                    <a href="tel:++18001061616">1800-102-1616</a>
                  </span>
                </p>
                <p className="success-modal-note-pill">
                  <span className="success-modal-note-pill-text m-0">
                    Please note that the exchange rate shown shall be blocked
                    only for 24 hrs*
                  </span>
                </p>
                <div className="delete-modal-item">
                  <button
                    className="delete-modal-no-button"
                    onClick={() => {
                      // setUserDetails({
                      //   userName: '',
                      //   userPhone: '',
                      //   userCity: '',
                      //   userState: '',
                      //   userEmail: ' ',
                      // });
                      // setLeadState({
                      //   items: [],
                      // });
                      closeSuccessModalRef.current?.click();
                      handleStep(activeStep - 1);
                    }}
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </>
  );
};

export default CallbackForm;
